import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { GraduationCap, FileDown, PlayCircle, ChevronDown, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Formation() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/guide/formation").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Chargement impossible"));
  }, []);

  const startTour = (name, path) => { localStorage.setItem("biashara.pendingTour", name); navigate(path); };

  return (
    <div className="space-y-6" data-testid="formation-page">
      <div className="relative overflow-hidden rounded-2xl p-6 text-white shadow-lg" style={{ background: "linear-gradient(160deg,#1A3A5C 0%,#16324F 55%,#0E2135 100%)" }}>
        <div className="pointer-events-none absolute -right-16 -top-16 w-52 h-52 rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/70"><GraduationCap className="w-4 h-4" /> Formation des vendeurs</div>
            <h1 className="text-2xl md:text-3xl font-bold font-head mt-1.5">Prendre en main Biashara</h1>
            <p className="text-sm text-white/75 mt-1">Parcours pas à pas des fonctions clés. Suivez le guide ou lancez la visite guidée du point de vente.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => startTour("pos", "/pos")} className="inline-flex items-center gap-2 rounded-xl bg-white text-[#16324F] font-semibold text-sm px-4 py-2.5 shadow hover:bg-white/90 transition-colors" data-testid="start-pos-tour-btn"><PlayCircle className="w-4 h-4" /> Visite guidée du POS</button>
            <button onClick={() => startTour("cash", "/cash")} className="inline-flex items-center gap-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 text-white font-semibold text-sm px-4 py-2.5 backdrop-blur transition-colors" data-testid="start-cash-tour-btn"><PlayCircle className="w-4 h-4" /> Visite guidée de la Caisse</button>
            <button onClick={() => startTour("quotes", "/quotes")} className="inline-flex items-center gap-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 text-white font-semibold text-sm px-4 py-2.5 backdrop-blur transition-colors" data-testid="start-quotes-tour-btn"><PlayCircle className="w-4 h-4" /> Visite guidée des Devis</button>
            <button onClick={() => window.open(`${BACKEND}/api/guide/formation.pdf`, "_blank", "noopener,noreferrer")} className="inline-flex items-center gap-2 rounded-xl bg-white/15 hover:bg-white/25 border border-white/25 text-white font-semibold text-sm px-4 py-2.5 backdrop-blur transition-colors" data-testid="download-guide-pdf-btn"><FileDown className="w-4 h-4" /> Télécharger le PDF</button>
          </div>
        </div>
      </div>

      {!data ? (
        <div className="card p-10 text-center text-[#94A3B8]">Chargement du guide…</div>
      ) : (
        <div className="space-y-3">
          {data.sections.map((s, idx) => {
            const isOpen = open === idx;
            return (
              <div key={s.id} className="card overflow-hidden" data-testid={`formation-section-${s.id}`}>
                <button onClick={() => setOpen(isOpen ? -1 : idx)} className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-slate-50 transition-colors" data-testid={`formation-toggle-${s.id}`}>
                  <span className="font-semibold text-[#16324F]">{s.title}</span>
                  <ChevronDown className={`w-5 h-5 text-[#64748B] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1 border-t border-[#F1F5F9]">
                    <ol className="space-y-2 mt-2">
                      {s.steps.map((st, k) => (
                        <li key={k} className="flex gap-3 text-sm text-[#334155]">
                          <span className="shrink-0 w-6 h-6 rounded-full bg-[#EFF6FF] text-[#2563EB] font-bold text-xs grid place-items-center">{k + 1}</span>
                          <span className="pt-0.5">{st}</span>
                        </li>
                      ))}
                    </ol>
                    {(s.tips || []).map((t, k) => (
                      <div key={k} className="mt-2 flex items-start gap-2 text-xs text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-2.5">
                        <Lightbulb className="w-4 h-4 shrink-0 mt-0.5" /><span>{t}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
